// The desktop app (emcog-desktop, Oct 23 2026) shows this same page in its own window and marks it with
// window.__EMCOG_DESKTOP__. There the page can ask the app for a few things — a Windows notification, Hands on this
// computer, updates — through the app's commands, which it grants only at this EmCog's own address.

interface DesktopMark { version: string }

const mark = (): DesktopMark | undefined => (window as any).__EMCOG_DESKTOP__;
const internals = (): { invoke: (cmd: string, args?: Record<string, unknown>) => Promise<any> } | undefined => (window as any).__TAURI_INTERNALS__;

/** Is this page inside the desktop app? */
export const inDesktop = (): boolean => !!mark() && !!internals();

/** Ask the desktop app for something (rejects outside it). */
export function desktopCall<T = any>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  const ipc = internals();
  if (!inDesktop() || !ipc) return Promise.reject(new Error('Not in the desktop app.'));
  return ipc.invoke(cmd, args || {});
}

/**
 * The click handler for a link that leaves this page (<a target="_blank">). In a browser the link opens itself. In
 * the desktop app the click becomes a window request the app answers: a new tab opens in the default browser, and
 * `ownWindow` asks for a sized window, which the app opens as a second window of its own (apps from 1.0.3 — older
 * ones use the browser). Left to itself, the click was taken by the app's link script, which asked for a command
 * this page is never granted, so nothing opened (apps up to 1.0.2).
 */
export function openLinkFromDesktop(e: { preventDefault(): void; currentTarget: HTMLAnchorElement }, ownWindow = false): void {
  if (!inDesktop()) return;
  e.preventDefault();
  const url = e.currentTarget.href;
  if (ownWindow) window.open(url, '_blank', `width=${Math.max(640, window.innerWidth)},height=${Math.max(480, window.innerHeight)}`);
  else window.open(url, '_blank', 'noopener');
}

export interface DesktopInfo {
  version: string;
  server: string | null;
  autostart: boolean;
  shortcut: string;
  updates: { pending: string | null; last: string };
  updatesWork: boolean;
}

export interface HandsStatus {
  paired: boolean;
  server: string | null;
  name: string | null;
  host: string;
  connected: boolean;
  paused: boolean;
  lines: string[];
  note: string | null;
  version: string;
}
